// How an "Any Fitter" booking gets its fitter.
//
// Under Any Fitter the customer chooses a TIME; the widget chooses who takes
// it, from the fitters at the studio who have that time open. Two rules:
//
//   random   The default, and the only behavior before this file existed: one
//            of the available fitters, uniformly at random, which spreads
//            bookings across the roster.
//
//   popular  Opt-in per mount with `data-cc-fitter-assignment="popular"`. The
//            available fitter with the most fittings on record takes the time;
//            when that fitter does not have the time open it goes to the next
//            most popular who does, and so on down the roster. Fitters level
//            on count (including the case where no counts arrived at all) are
//            split at random, so a missing count degrades to the default rule
//            rather than to "always the first fitter in the list".
//
// The count is `fittingCount` on each fitter from the middleware's /resources.
// A fitter the customer picks by name is never reassigned; this only governs
// the choice the widget makes on the customer's behalf.

import { createContext } from 'react';

export type FitterAssignment = 'random' | 'popular';

export const DEFAULT_FITTER_ASSIGNMENT: FitterAssignment = 'random';

/** Attribute on an inline container or a modal trigger that opts in. */
export const FITTER_ASSIGNMENT_ATTR = 'data-cc-fitter-assignment';

/** Anything but an explicit "popular" keeps the default. */
export function parseFitterAssignment(value: string | null | undefined): FitterAssignment {
  return value?.trim().toLowerCase() === 'popular' ? 'popular' : DEFAULT_FITTER_ASSIGNMENT;
}

export function readFitterAssignment(el: Element | null): FitterAssignment {
  return parseFitterAssignment(el?.getAttribute(FITTER_ASSIGNMENT_ATTR));
}

/**
 * The rule in force for the Widget instance below it. A context rather than a
 * prop because the one consumer, the time-slot grid, sits several components
 * down from the mount that knows the answer.
 */
export const FitterAssignmentContext = createContext<FitterAssignment>(DEFAULT_FITTER_ASSIGNMENT);

/**
 * Choose who takes one time, from the fitters who all have it open.
 * `candidates` must be non-empty.
 */
export function pickFitterForSlot<T extends { fitterId: string }>(
  candidates: T[],
  mode: FitterAssignment,
  fittingCountOf: (fitterId: string) => number | undefined,
  random: () => number = Math.random,
): T {
  let pool = candidates;
  if (mode === 'popular' && candidates.length > 1) {
    let best = -1;
    let leaders: T[] = [];
    for (const c of candidates) {
      const count = fittingCountOf(c.fitterId) ?? 0;
      if (count > best) {
        best = count;
        leaders = [c];
      } else if (count === best) {
        leaders.push(c);
      }
    }
    pool = leaders;
  }
  return pool[Math.floor(random() * pool.length)];
}
